import React, { useEffect, useState } from 'react';
import { Modal, Button, Message, Loader } from 'semantic-ui-react';
import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
    baseURL: 'https://qa-tenantbridge.parablu.com',
    withCredentials: true
});

const ApprovalSuccessModal = ({
    open,
    onClose,
    requestData,
    userEmail,
    requestCategory
}) => {
    const [userRoles, setUserRoles] = useState([]);
    const [otherApprovers, setOtherApprovers] = useState([]);
    const [isLastApprover, setIsLastApprover] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!open || !userEmail || !requestCategory) return;

            setLoading(true);
            try {
                // Use api instance with correct base URL
                const rolesResponse = await api.get('/api/user/roles');

                if (rolesResponse.data.roles) {
                    setUserRoles(rolesResponse.data.roles);

                    // Get other approvers
                    const approversResponse = await api.get(`/api/category-approvers/${encodeURIComponent(requestCategory)}`);

                    if (approversResponse.data.approvers) {
                        const categoryApprovers = approversResponse.data.approvers.filter(email =>
                            email.toLowerCase() !== userEmail.toLowerCase() &&
                            email.toLowerCase() !== requestData?.formData?.submittedBy?.toLowerCase()
                        );
                        setOtherApprovers(categoryApprovers);
                        setIsLastApprover(categoryApprovers.length === 0);
                    }
                }
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open, userEmail, requestCategory, requestData]);

    const getMessage = () => {
        const isApprover = userRoles.includes('APPROVER');

        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Loader active inline='centered' />
                    <p style={{ marginTop: '10px' }}>Processing approval details...</p>
                </div>
            );
        }

        // Handle approver case
        if (isApprover) {
            return (
                <Message positive>
                    <Message.Header>Approval Successful</Message.Header>
                    <div style={{ marginTop: '1rem' }}>
                        <p>Your approval has been recorded successfully.</p>
                        {otherApprovers.length > 0 ? (
                            <p>Other approver(s) may also need to approve this request.</p>
                        ) : (
                            <p>The request will proceed to the main approval stage.</p>
                        )}
                        <p>You can track the status in your Approval History.</p>
                    </div>
                </Message>
            );
        }

        // Handle approver case
        if (isApprover) {
            return (
                <Message positive>
                    <Message.Header>Approval Successful</Message.Header>
                    <div style={{ marginTop: '1rem' }}>
                        {isLastApprover ? (
                            <>
                                <p>You were the last approver for this request.</p>
                                <p>The VM creation process will begin shortly.</p>
                            </>
                        ) : (
                            <>
                                <p>Your approval has been recorded successfully.</p>
                                <p>The request has been sent to {otherApprovers.length} remaining approver(s).</p>
                            </>
                        )}
                        <p>You can track the progress in your Approval History.</p>
                    </div>
                </Message>
            );
        }

        // Default message with debug info
        return (
            <Message positive>
                <Message.Header>Request Processed</Message.Header>
                <div style={{ marginTop: '1rem' }}>
                    <p>The approval has been processed successfully.</p>
                    <p>Current roles: {userRoles.join(', ')}</p>
                    <p>Request status: {requestData?.status}</p>
                    <p>You can track all updates in your Approval History.</p>
                </div>
            </Message>
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="small"
            closeOnDimmerClick={false}
        >
            <Modal.Header style={{
                backgroundColor: '#f0fdf4',
                color: '#6F9911'
            }}>
                Approval Status
            </Modal.Header>
            <Modal.Content>
                {getMessage()}
            </Modal.Content>
            <Modal.Actions>
                <Button
                    color="green"
                    onClick={onClose}
                    disabled={loading}
                >
                    {loading ? 'Please wait...' : 'Close'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default ApprovalSuccessModal;